import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGoogleFont } from '@/features/customization/hooks/useGoogleFont';
import {
  parseCustomization,
  toBrandCssVars,
} from '@/features/customization/schemas/customizationSchemas';
import { usePublicWorkflowIntro } from '../api/usePublicWorkflowIntro';
import type { SessionEntryResult } from '../api/useSessionOtp';
import { SESSION_ENTRY_OUTCOME } from '../api/useSessionOtp';
import { MobileOnlyGate } from '../components/MobileOnlyGate';
import {
  AlreadyVerifiedNotice,
  MobileVerificationGate,
} from '../components/MobileVerificationGate';
import { PublicWelcomeScreen } from '../components/WelcomeIntro';
import { markIntroSeen } from '../components/welcomeSteps';
import { useNoZoomViewport } from '../hooks/useNoZoomViewport';

// Entry point for the ONE shared workflow link: /wf/:slug. The same link is used
// everywhere — QR, Copy link, an emailed invite, a bulk send.
//
// There is no per-recipient invite link any more: emails, bulk sends, the QR and
// Copy link all carry THIS url, and the mobile number proves who the applicant is.
// (The old ?s=<token> shortcut jumped straight into a named session with no OTP —
// holding the URL stood in for proving the phone, so it's gone.)
//
// Screen order: the WELCOME OVERVIEW first, then the mobile + OTP gate, then the run's
// steps. The overview comes first on purpose — the applicant sees what will be asked
// of them, and what data it touches, before handing over a phone number. It runs on
// the provisional intro, so nothing is written server-side by looking at it.
//
//   • normally     → overview, then verify a MOBILE NUMBER. Nothing exists
//                    server-side until the code is confirmed; that call then decides
//                    between creating this number's run, resuming its in-progress
//                    one, or reporting that the number has already been through.
//   • ?resumed=1   → came back from a same-tab redirect (e.g. DigiLocker): resume
//                    this browser's remembered token without asking again.
//   • ?ref=<label> → tags the created session for tracking.
const tokenKey = (slug: string) => `sc.run.${slug}`;

export function PublicWorkflowEntryScreen() {
  // App-like: no pinch / double-tap zoom on the welcome + OTP screens either.
  useNoZoomViewport();

  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const refId = params.get('ref') ?? undefined;
  // ?i= identifies WHICH emailed invite this visit came from, so the Sent list can
  // mark it started. It authorises nothing — the OTP below is still required.
  const inviteToken = params.get('i') ?? undefined;
  const resumed = params.get('resumed') === '1';
  const storedToken = (() => {
    if (!resumed) return null;
    try {
      return localStorage.getItem(tokenKey(slug));
    } catch {
      return null;
    }
  })();

  // A number that verified but has already used up its one run — we show the
  // outcome instead of a flow.
  const [alreadyUsedStatus, setAlreadyUsedStatus] = useState<string | null>(null);

  // False until Continue on the overview. The OTP gate is only reachable through it,
  // so a number is never requested before the applicant has seen what it is for.
  const [introAcknowledged, setIntroAcknowledged] = useState(false);

  // Returned from a same-tab redirect → hand back to the per-session run.
  useEffect(() => {
    if (!storedToken) return;
    navigate(`/w/${storedToken}`, { replace: true });
  }, [storedToken, navigate]);

  // Workflow branding + options for the pre-session screens. Creates nothing —
  // it exists so the OTP gate is branded and the mobile-only rule still applies
  // before a session exists. Skipped while a token redirect is in flight.
  const {
    data: intro,
    isLoading,
    isError,
  } = usePublicWorkflowIntro(storedToken ? undefined : slug);

  const branding = parseCustomization(intro?.customization?.customizationJson);
  const brandVars = toBrandCssVars(branding);
  useGoogleFont(intro?.customization ? branding.typographyStyle : undefined);

  // The code has been verified: the server has created or resumed the session, so
  // remember its token for same-tab redirects and hand off to the normal run.
  const handleVerified = (result: SessionEntryResult) => {
    if (result.outcome === SESSION_ENTRY_OUTCOME.AlreadyUsed) {
      setAlreadyUsedStatus(result.session.status);
      return;
    }
    const token = result.session.publicToken;
    if (!token) return;
    try {
      localStorage.setItem(tokenKey(slug), token);
    } catch {
      // ignore — same-browser resume just won't work next time
    }
    // The overview was the first screen, so the run must not open on it again —
    // that would read as the flow having restarted after the OTP.
    markIntroSeen(token);
    navigate(`/w/${token}`, { replace: true });
  };

  // While a token redirect is in flight (invite link or same-tab return), spin.
  if (storedToken) {
    return <CenteredSpinner />;
  }

  return (
    // Same scoped frame as the run itself (`sc-session-preview-page`, see
    // styles/globals.css): on a phone this is pure white at exactly 100dvh with the
    // card's body as the only scrolling element, and the tinted page + floating card
    // return from 481px up. It replaces the `min-h-dvh bg-[#F1F5FB]` that used to be
    // here — that tint plus the one the welcome/OTP cards paint themselves is what
    // put blue bands on all four sides of a white sheet.
    <div className="sc-session-preview-page brand-scope relative" style={brandVars}>
      <div className="flex min-h-0 w-full flex-1 flex-col">
        {intro ? (
          // Workflow Settings → "Allow desktop access". Gated before the OTP gate
          // so a mobile-only workflow never even sends a code to a desktop visitor.
          <MobileOnlyGate allowDesktop={intro.options?.allowDesktopAccess ?? true}>
            {alreadyUsedStatus ? (
              <AlreadyVerifiedNotice status={alreadyUsedStatus} />
            ) : introAcknowledged ? (
              <MobileVerificationGate
                slug={slug}
                refId={refId}
                inviteToken={inviteToken}
                onVerified={handleVerified}
              />
            ) : (
              <PublicWelcomeScreen intro={intro} onContinue={() => setIntroAcknowledged(true)} />
            )}
          </MobileOnlyGate>
        ) : isLoading ? (
          <CenteredSpinner />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-[length:var(--text-16)] font-semibold text-black-logo">
              This link isn't available
            </p>
            <p className="max-w-sm text-[length:var(--text-14)] text-gray66">
              {isError
                ? 'The verification link is invalid, or this workflow has been archived. Please contact the sender for a new link.'
                : 'Verification not found.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function CenteredSpinner() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-grayEF/30 px-6 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <p className="text-[length:var(--text-14)] text-gray66">Starting your verification…</p>
    </div>
  );
}
